// Screen Controller - Manages screen swapping and event delegation
import { store } from '../app/store.js';
import { Email } from '../domain/valueObjects.js';
import Alpine from 'alpinejs';
import { formatMoney, updateDisclaimerPrices } from '../utils/currency.js';
import { setCurrentCurrency, setCurrentCurrencyFromGeo, getCurrentCurrency } from '../utils/currency-context.js';
import { waitForGeoCountry } from '../utils/geo.js';
import { CoinPiles } from '../components/CoinPiles.js';
import type { FaceLandmarksController } from '../components/face-landmarks/adapter.js';
import { buildCheckoutMetadata } from '../utils/checkout-metadata.js';
import { getPaywallPlanLabels } from '../config/paywall-config.js';
import { identifyAmplitude } from '../app/amplitude.js';

export class ScreenController {
  private root: HTMLElement;
  private currentScreenElement: HTMLElement | null = null;
  private countdownTimerId: number | null = null;
  private currentScreen: any = null;
  private voucherTimerIds: Map<string, number> = new Map();
  private coinPiles: CoinPiles | null = null;
  private faceLandmarksController: FaceLandmarksController | null = null;
  private cameraCountdownTimerId: number | null = null;
  private cameraProgressHandler: ((e: Event) => void) | null = null;
  private cameraCompleteHandler: (() => void) | null = null;

  constructor(rootElement: HTMLElement) {
    this.root = rootElement;
    this.currentScreenElement = null;
    this.setupEventDelegation();
  }

  swapScreen(screen) {
    const template = document.getElementById(screen.template);
    if (!template) {
      console.error(`Template ${screen.template} not found`);
      return;
    }

    // Clean up camera when leaving camera screen
    this.cleanupCamera();
    document.body.classList.remove('camera-active');

    // Store current screen
    this.currentScreen = screen;
    console.log('Swapping to screen:', screen);

    // Clone template content
    const screenContent = template.content.cloneNode(true);

    // Clear current content with transition
    this.root.style.opacity = '0';

    setTimeout(() => {
      // Replace content
      this.root.replaceChildren(screenContent);
      this.currentScreenElement = this.root.querySelector('[data-screen]');

      // Bind data if screen has data field
      if (screen.dataField) {
        this.bindScreenData(screen);
      }

      // Special handling for certain screens
      this.handleSpecialScreens(screen);

      // Fade in new content
      this.root.style.opacity = '1';

      // Focus for accessibility
      this.root.focus();

      // Dispatch screen loaded event
      document.dispatchEvent(new CustomEvent('screen:loaded', {
        detail: { screen }
      }));
    }, 150); // Match with CSS transition duration
  }

  setupEventDelegation() {
    // Handle clicks on action buttons
    this.root.addEventListener('click', (event) => {
      const target = event.target.closest('[data-action]');
      if (!target) return;

      const action = target.getAttribute('data-action');
      const value = target.getAttribute('data-value');

      this.handleAction(action, value, target, event);
    });

    // Handle input changes
    this.root.addEventListener('input', (event) => {
      const target = event.target;
      if (target.hasAttribute('data-bind')) {
        const field = target.getAttribute('data-bind');
        store.set(field, target.value);

        // Special validation for email field
        if (field === 'email') {
          this.validateEmailInput(target);
        }
      }
    });
  }

  handleAction(action, value, element, event) {
    event.preventDefault();

    switch (action) {
      case 'next':
        this.validateAndProceed();
        break;

      case 'back':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'back' } }));
        break;

      case 'skip':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'select':
        this.handleSelection(value, element);
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'toggle':
        this.handleToggle(value, element);
        break;

      case 'capture-selfie':
        document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
        break;

      case 'subscribe':
        this.openCheckout();
        break;

      case 'download-app':
        this.downloadApp();
        break;

      default:
        // Dispatch custom event for other actions
        document.dispatchEvent(new CustomEvent(`action:${action}`, {
          detail: { value, element }
        }));
    }
  }

  handleSelection(value, element) {
    // Remove selected class from siblings
    const parent = element.parentElement;
    parent.querySelectorAll('.option-card').forEach(card => {
      card.classList.remove('selected');
    });

    // Add selected class
    element.classList.add('selected');

    // Store value
    const screen = this.getCurrentScreen();
    if (screen?.dataField) {
      store.set(screen.dataField, value);
    }
  }

  handleToggle(value, element) {
    // Handle exclusive options
    if (element.classList.contains('exclusive')) {
      // If selecting "none", deselect all others
      const parent = element.parentElement;
      if (!element.classList.contains('active')) {
        parent.querySelectorAll('.selectable').forEach(card => {
          card.classList.remove('active');
        });
      }
    } else {
      // Deselect "none" if selecting other options
      const parent = element.parentElement;
      const noneOption = parent.querySelector('.exclusive');
      if (noneOption && noneOption.classList.contains('active')) {
        noneOption.classList.remove('active');
      }
    }

    // Toggle active class
    element.classList.toggle('active');

    // Collect all active values
    const parent = element.parentElement;
    const activeValues = Array.from(parent.querySelectorAll('.selectable.active'))
      .map(el => el.getAttribute('data-value'));

    // Store values
    const screen = this.getCurrentScreen();
    if (screen?.dataField) {
      store.set(screen.dataField, activeValues);
    }
  }

  validateAndProceed() {
    const screen = this.getCurrentScreen();

    // Check required fields
    if (screen?.required) {
      const input = this.root.querySelector(`[data-bind="${screen.dataField}"]`);
      if (!input || !input.value) {
        this.showValidationError('This field is required');
        return;
      }

      // Email validation using domain layer
      if (screen.validation === 'email') {
        if (!Email.isValid(input.value.trim())) {
          this.showValidationError('Please enter a valid email address');
          return;
        }

        // Store the validated email as lowercase (Email value object behavior)
        const emailValue = input.value.trim().toLowerCase();
        store.set(screen.dataField, emailValue);
        // Attach email as an Amplitude user property (not user_id)
        identifyAmplitude({ email: emailValue });
      }
    }

    // Proceed to next screen
    document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
  }

  showValidationError(message) {
    const validation = this.root.querySelector('.input-validation');
    if (validation) {
      validation.textContent = message;
      validation.classList.add('error');
    }
  }

  bindScreenData(screen) {
    const storedValue = store.get(screen.dataField);
    if (!storedValue) return;

    // For text inputs
    const input = this.root.querySelector(`[data-bind="${screen.dataField}"]`);
    if (input) {
      input.value = storedValue;
    }

    // For single select options
    if (!screen.multiSelect && typeof storedValue === 'string') {
      const option = this.root.querySelector(`[data-value="${storedValue}"]`);
      if (option) {
        option.classList.add('selected');
      }
    }

    // For multi-select options
    if (screen.multiSelect && Array.isArray(storedValue)) {
      storedValue.forEach(value => {
        const option = this.root.querySelector(`[data-value="${value}"]`);
        if (option) {
          option.classList.add('active');
        }
      });
    }
  }

  handleSpecialScreens(screen) {
    switch (screen.id) {
      case 'processing':
        this.startProgressAnimation(screen.duration || 8000);
        break;

      case 'analysis':
        this.displayAnalysisScreen();
        this.startAnalysisStepsAnimation(screen.duration || 15000);
        break;

      case 'social-proof':
        this.updateSocialProofText();
        break;

      case 'email':
        this.setupEmailValidation();
        break;

      case 'camera':
        document.body.classList.add('camera-active');
        this.initializeCamera();
        break;

      case 'results':
        this.displayResults();
        break;

      case 'paywall':
        this.initializePaywall();
        this.initializeSocialProof();
        break;

      case 'success':
        this.showSuccessAnimation();
        break;

      case 'prediction':
        this.initializePredictionAnimations();
        break;

      case 'smart-investment':
        this.initializeCoinPiles();
        break;
    }
  }

  startProgressAnimation(duration) {
    const stages = [
      { text: 'Analyzing your skin profile...', progress: 25 },
      { text: 'Reviewing your lifestyle factors...', progress: 50 },
      { text: 'Comparing with thousands of success stories...', progress: 75 },
      { text: 'Generating your personalized plan...', progress: 100 }
    ];

    let currentStage = 0;
    const interval = duration / stages.length;

    // Store the current screen element to check if we're still on the same screen
    const initialScreenElement = this.currentScreenElement;

    const updateStage = () => {
      // Check if we're still on the same screen
      if (this.currentScreenElement !== initialScreenElement) {
        // User navigated away, stop the animation
        return;
      }

      if (currentStage >= stages.length) {
        // Double-check we're still on the same screen before navigating
        if (this.currentScreenElement === initialScreenElement) {
          setTimeout(() => {
            // Final check before navigation
            if (this.currentScreenElement === initialScreenElement) {
              document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
            }
          }, 500);
        }
        return;
      }

      const stage = stages[currentStage];
      const textElement = this.root.querySelector('#processing-text, #analysis-text');
      const progressBar = this.root.querySelector('#analysis-bar');
      const progressPercent = this.root.querySelector('#progress-percentage');
      const progressRing = this.root.querySelector('#analysis-progress');

      if (textElement) textElement.textContent = stage.text;
      if (progressBar) progressBar.style.width = `${stage.progress}%`;
      if (progressPercent) progressPercent.textContent = `${stage.progress}%`;
      if (progressRing) {
        const offset = 283 - (283 * stage.progress / 100);
        progressRing.style.strokeDashoffset = offset;
      }

      currentStage++;
      setTimeout(updateStage, interval);
    };

    updateStage();
  }

  updateSocialProofText() {
    const goal = store.get('wq_primary_goal');
    const goalText = this.root.querySelector('#user-goal-text');

    if (goalText && goal) {
      const goalMap = {
        'clear-acne': 'clear breakouts and prevent acne',
        'reduce-aging': 'reduce fine lines and wrinkles',
        'even-tone': 'even out skin tone',
        'hydrate': 'hydrate and nourish skin',
        'calm-redness': 'calm redness and sensitivity',
        'skip': 'skin improvement'
      };

      goalText.textContent = goalMap[goal] || 'similar goals';
    }
  }

  setupEmailValidation() {
    const emailInput = this.root.querySelector('#email-input');
    const submitButton = this.root.querySelector('#email-submit');

    if (emailInput && submitButton) {
      // Initially disable the button
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');

      // Pressing Enter submits when the email is valid (no surrounding <form>)
      emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          if (!submitButton.disabled) {
            submitButton.click();
          }
        }
      });

      // Validate existing value if any
      if (emailInput.value) {
        this.validateEmailInput(emailInput);
      }
    }
  }

  validateEmailInput(input) {
    const submitButton = this.root.querySelector('#email-submit');
    const validation = this.root.querySelector('#email-validation');
    const arrowButton = this.root.querySelector('#email-arrow-btn');

    if (!submitButton) return;

    const value = input.value.trim();
    const isValid = !!value && Email.isValid(value);

    // Enable the submit button + reveal the in-input arrow only when valid
    submitButton.disabled = !isValid;
    submitButton.classList.toggle('btn-disabled', !isValid);
    if (arrowButton) {
      arrowButton.classList.toggle('is-visible', isValid);
    }

    // Show the error only once something invalid has been typed
    if (validation) {
      if (value && !isValid) {
        validation.textContent = 'Please enter a valid email address';
        validation.classList.add('error');
      } else {
        validation.textContent = '';
        validation.classList.remove('error');
      }
    }
  }

  async initializeCamera() {
    // Clean up any existing controller
    if (this.faceLandmarksController) {
      this.faceLandmarksController.dispose();
      this.faceLandmarksController = null;
    }

    const container = this.root.querySelector('#face-landmarks-container');
    const loadingSpinner = this.root.querySelector('#camera-loading');

    if (!container) {
      console.error('Face landmarks container not found');
      return;
    }

    // Show loading spinner
    if (loadingSpinner) {
      loadingSpinner.classList.remove('hidden');
    }

    // Listen for progress events to update local progress bar
    const progressFill = this.root.querySelector('#camera-progress-fill') as HTMLElement;
    const progressPercent = this.root.querySelector('#camera-progress-percent');
    const handleProgress = (e: Event) => {
      const percent = (e as CustomEvent).detail.percent;
      if (progressFill) {
        progressFill.style.width = `${percent}%`;
      }
      if (progressPercent) {
        progressPercent.textContent = `${Math.round(percent)}%`;
      }
    };
    this.cameraProgressHandler = handleProgress;
    document.addEventListener('face-landmarks:progress', handleProgress);

    // Swap the "Skip" button for the auto-advancing "Continue" button on completion
    const skipBtn = this.root.querySelector('#camera-skip-btn') as HTMLElement | null;
    const continueBtn = this.root.querySelector('#camera-continue-btn') as HTMLElement | null;

    // "Skip" does exactly what the camera-unavailable popup does: jump to the
    // fallback screen (results, with mock data) via the shared skipCamera() method
    const skipButtonEl = skipBtn?.querySelector('button') as HTMLButtonElement | null;
    if (skipButtonEl) {
      skipButtonEl.addEventListener('click', () => this.skipCamera());
    }
    const handleComplete = () => {
      if (skipBtn) {
        skipBtn.style.display = 'none';
      }
      if (continueBtn) {
        continueBtn.style.display = 'block';
        this.startCameraContinueCountdown(continueBtn);
      }
      document.removeEventListener('face-landmarks:complete', handleComplete);
      document.removeEventListener('face-landmarks:progress', handleProgress);
      this.cameraProgressHandler = null;
      this.cameraCompleteHandler = null;
    };
    this.cameraCompleteHandler = handleComplete;
    document.addEventListener('face-landmarks:complete', handleComplete);

    try {
      const { initFaceLandmarksCamera } = await import('../components/face-landmarks/adapter.js');
      this.faceLandmarksController = await initFaceLandmarksCamera(container as HTMLElement);

      // Hide loading spinner once models are loaded
      if (loadingSpinner) {
        loadingSpinner.classList.add('hidden');
      }

      this.faceLandmarksController.start();
    } catch (error) {
      // Hide loading spinner on error
      if (loadingSpinner) {
        loadingSpinner.classList.add('hidden');
      }

      console.error('Face landmarks initialization failed:', error);
      const err = error as DOMException;
      let message = 'Camera access is required for skin analysis.';
      if (err.name === 'NotFoundError') {
        message = 'No camera found on this device.';
      } else if (err.name === 'NotAllowedError') {
        message = 'Camera access was denied. Please enable camera permissions.';
      }
      document.removeEventListener('face-landmarks:complete', handleComplete);
      document.removeEventListener('face-landmarks:progress', handleProgress);
      this.cameraProgressHandler = null;
      this.cameraCompleteHandler = null;
      this.showCameraErrorDialog(message);
    }
  }

  /**
   * Once the scan completes, the Continue button shows a "Continue (N)" countdown
   * and auto-advances when it reaches zero. Duration comes from flow.ts
   * (`autoContinueCountdown`, default 5s); set it to 0 to disable auto-advance.
   */
  private startCameraContinueCountdown(continueBtn: HTMLElement) {
    const buttonEl = continueBtn.querySelector('button') as HTMLButtonElement | null;
    if (!buttonEl) return;

    const baseLabel = (buttonEl.textContent || '').trim() || 'Continue';
    const seconds = Number(this.currentScreen?.autoContinueCountdown ?? 5);

    // Auto-advance disabled: leave the plain Continue label in place
    if (!Number.isFinite(seconds) || seconds <= 0) {
      buttonEl.textContent = baseLabel;
      return;
    }

    let remaining = Math.floor(seconds);
    const render = () => {
      buttonEl.textContent = `${baseLabel} (${remaining})`;
    };
    render();

    this.clearCameraCountdown();
    this.cameraCountdownTimerId = window.setInterval(() => {
      remaining -= 1;
      if (remaining > 0) {
        render();
        return;
      }
      // Reached zero: stop the timer and trigger the normal Continue action
      this.clearCameraCountdown();
      buttonEl.click();
    }, 1000);
  }

  private clearCameraCountdown() {
    if (this.cameraCountdownTimerId !== null) {
      clearInterval(this.cameraCountdownTimerId);
      this.cameraCountdownTimerId = null;
    }
  }

  private showCameraErrorDialog(message: string): void {
    // Create dialog overlay (styles in components.css)
    const overlay = document.createElement('div');
    overlay.className = 'camera-error-overlay';
    overlay.innerHTML = `
      <div class="camera-error-dialog">
        <p class="camera-error-message">${message}</p>
        <div class="camera-error-buttons">
          <button class="btn btn-secondary" data-action="skip">Skip</button>
          <button class="btn btn-primary" data-action="retry">Try Again</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    // Handle button clicks
    overlay.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const action = target.dataset.action;

      if (action === 'retry') {
        overlay.remove();
        window.location.reload();
      } else if (action === 'skip') {
        overlay.remove();
        this.skipCamera();
      }
    });
  }

  /** Skip the camera scan: go to the configured fallback screen (flow.ts permissionDeniedSkipTo). */
  private skipCamera() {
    this.cleanupCamera();
    document.body.classList.remove('camera-active');
    const skipToScreen = this.currentScreen?.permissionDeniedSkipTo || 'analysis';
    window.location.hash = `#${skipToScreen}`;
  }

  cleanupCamera() {
    // Stop a pending Continue countdown so it can't auto-advance after we leave
    this.clearCameraCountdown();

    // Detach scan listeners if we leave before completion (e.g. user taps Skip)
    if (this.cameraProgressHandler) {
      document.removeEventListener('face-landmarks:progress', this.cameraProgressHandler);
      this.cameraProgressHandler = null;
    }
    if (this.cameraCompleteHandler) {
      document.removeEventListener('face-landmarks:complete', this.cameraCompleteHandler);
      this.cameraCompleteHandler = null;
    }

    if (this.faceLandmarksController) {
      this.faceLandmarksController.dispose();
      this.faceLandmarksController = null;
    }
  }

  displayAnalysisScreen() {
    // Display selfies on analysis screen (with and without mesh)
    const selfieWithMesh = store.get('selfie') as string | undefined;
    const selfieWithoutMesh = store.get('selfieWithoutMesh') as string | undefined;
    const selfieBottom = this.root.querySelector('#selfie-bottom') as HTMLImageElement;
    const selfieTop = this.root.querySelector('#selfie-top') as HTMLImageElement;

    if (selfieBottom && selfieTop && selfieWithMesh) {
      // Start with mesh on bottom, without mesh on top (hidden via clip-path)
      selfieBottom.src = selfieWithMesh;
      selfieTop.src = selfieWithoutMesh || selfieWithMesh;

      // Animate the reveal synced with scan line
      this.startAnalysisRevealAnimation(selfieTop);
    }
  }

  private startAnalysisRevealAnimation(topImage: HTMLImageElement) {
    const selfieWithMesh = store.get('selfie') as string | undefined;
    const selfieWithoutMesh = store.get('selfieWithoutMesh') as string | undefined;
    const bottomImage = this.root.querySelector('#selfie-bottom') as HTMLImageElement;

    if (!selfieWithMesh || !selfieWithoutMesh || !bottomImage) return;

    const cycleDuration = 3000; // Match CSS animation duration (3s)
    let showingMesh = true; // Bottom starts with mesh
    let animationId: number;
    let startTime: number;

    // CSS ease-in-out function to match scan line animation
    const easeInOut = (t: number): number => {
      return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    };

    // Offset to align with scan line bar (not the glow below it)
    // The scan line has a ::before glow that extends ~20px below, so we offset by ~7% of image height
    const barOffset = 3;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const cycleProgress = (elapsed % cycleDuration) / cycleDuration;

      // Apply ease-in-out to match CSS animation timing
      // CSS animation: 0% -> top:0%, 100% -> top:100% with ease-in-out
      const easedProgress = easeInOut(cycleProgress);
      // Offset the reveal to align with the bar, not the glow
      const revealPercent = Math.max(0, Math.min(100, easedProgress * 100 - barOffset));
      topImage.style.clipPath = `inset(0 0 ${100 - revealPercent}% 0)`;

      // When cycle completes (scan line reaches bottom), swap images
      if (cycleProgress < 0.05 && elapsed > cycleDuration * 0.5) {
        // Cycle just reset - swap the images for next reveal
        showingMesh = !showingMesh;
        if (showingMesh) {
          bottomImage.src = selfieWithMesh;
          topImage.src = selfieWithoutMesh;
        } else {
          bottomImage.src = selfieWithoutMesh;
          topImage.src = selfieWithMesh;
        }
        // Reset clip-path for new cycle
        topImage.style.clipPath = 'inset(0 0 100% 0)';
        startTime = timestamp;
      }

      animationId = requestAnimationFrame(animate);
    };

    animationId = requestAnimationFrame(animate);

    // Clean up when leaving screen
    const cleanup = () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener('screen:change', cleanup);
    };
    document.addEventListener('screen:change', cleanup);
  }

  private startAnalysisStepsAnimation(duration: number) {
    const steps = this.root.querySelectorAll('.analysis-step');
    const stepCount = steps.length || 4; // Default to 4 if not found
    const stepDuration = duration / stepCount;

    // Store the current screen element to check if we're still on the same screen
    const initialScreenElement = this.currentScreenElement;

    const startTime = Date.now();
    let animationId: number;

    // Start feedback carousel
    this.startFeedbackCarousel(duration);

    const animate = () => {
      // Check if we're still on the same screen
      if (this.currentScreenElement !== initialScreenElement) {
        return;
      }

      const now = Date.now();
      const totalElapsed = now - startTime;

      // Calculate which step we should be on based on total elapsed time
      const expectedStep = Math.min(Math.floor(totalElapsed / stepDuration), stepCount - 1);

      // Calculate progress within current step
      const stepStartTime = expectedStep * stepDuration;
      const stepElapsed = totalElapsed - stepStartTime;
      const stepProgress = Math.min(stepElapsed / stepDuration, 1);

      // Fill all completed steps to 100%
      for (let i = 0; i < expectedStep; i++) {
        const fill = this.root.querySelector(`#step-fill-${i + 1}`) as HTMLElement;
        if (fill) {
          fill.style.width = '100%';
        }
        if (steps[i]) {
          steps[i].classList.remove('active');
          steps[i].classList.add('completed');
        }
      }

      // Update current step progress
      const currentFill = this.root.querySelector(`#step-fill-${expectedStep + 1}`) as HTMLElement;
      if (currentFill) {
        currentFill.style.width = `${stepProgress * 100}%`;
      }

      // Update current step class
      if (steps[expectedStep]) {
        steps[expectedStep].classList.remove('completed');
        steps[expectedStep].classList.add('active');
      }

      // Check if all steps complete (total duration elapsed)
      if (totalElapsed >= duration) {
        // Ensure all bars are filled to 100%
        for (let i = 0; i < stepCount; i++) {
          const fill = this.root.querySelector(`#step-fill-${i + 1}`) as HTMLElement;
          if (fill) {
            fill.style.width = '100%';
          }
          if (steps[i]) {
            steps[i].classList.remove('active');
            steps[i].classList.add('completed');
          }
        }
        // Navigate to next screen
        setTimeout(() => {
          if (this.currentScreenElement === initialScreenElement) {
            document.dispatchEvent(new CustomEvent('ui:navigate', { detail: { direction: 'next' } }));
          }
        }, 500);
        return;
      }

      animationId = requestAnimationFrame(animate);
    };

    // Start first step as active
    if (steps[0]) {
      steps[0].classList.add('active');
    }

    animationId = requestAnimationFrame(animate);

    // Clean up when leaving screen
    const cleanup = () => {
      cancelAnimationFrame(animationId);
      document.removeEventListener('screen:change', cleanup);
    };
    document.addEventListener('screen:change', cleanup);
  }

  private startFeedbackCarousel(duration: number) {
    const feedbackItems = this.root.querySelectorAll('.feedback-item');
    const feedbackCount = feedbackItems.length || 5;
    const feedbackDuration = duration / feedbackCount;

    const initialScreenElement = this.currentScreenElement;
    let currentFeedback = 0;
    let timerId: number;

    const showNextFeedback = () => {
      if (this.currentScreenElement !== initialScreenElement) {
        return;
      }

      // Mark current as exiting
      feedbackItems[currentFeedback]?.classList.remove('active');
      feedbackItems[currentFeedback]?.classList.add('exiting');

      // Move to next feedback
      currentFeedback = (currentFeedback + 1) % feedbackCount;

      // After transition, remove exiting class and show new one
      setTimeout(() => {
        feedbackItems.forEach(item => item.classList.remove('exiting'));
        feedbackItems[currentFeedback]?.classList.add('active');
      }, 400); // Match CSS transition duration

      // Schedule next if not at the end
      if (currentFeedback < feedbackCount - 1 || currentFeedback === 0) {
        timerId = window.setTimeout(showNextFeedback, feedbackDuration);
      }
    };

    // Start the carousel after first interval
    timerId = window.setTimeout(showNextFeedback, feedbackDuration);

    // Clean up when leaving screen
    const cleanup = () => {
      clearTimeout(timerId);
      document.removeEventListener('screen:change', cleanup);
    };
    document.addEventListener('screen:change', cleanup);
  }

  displayResults() {
    // Display user selfie or fallback image
    const selfie = store.get('selfie');
    const userSelfieImg = this.root.querySelector('#user-selfie') as HTMLImageElement | null;
    const potentialSelfieImg = this.root.querySelector('#potential-selfie') as HTMLImageElement | null;
    const fallbackImage = '/images/face_with_zones.jpg';

    if (userSelfieImg) {
      userSelfieImg.src = selfie || fallbackImage;
    }
    if (potentialSelfieImg) {
      potentialSelfieImg.src = selfie || fallbackImage;
      if (selfie) {
        potentialSelfieImg.style.filter = 'brightness(1.1) contrast(0.95)';
      }
    }
  }

  async initializePaywall() {
    // Import paywall service and logger dynamically
    try {
      const [serviceModule, loggerModule] = await Promise.all([
        import('../domain/paywall-service.js'),
        import('../utils/logger.js')
      ]);

      const { paywallService } = serviceModule;
      const { paywallLogger } = loggerModule;

      paywallLogger.log('Initializing Paywall screen');

      // Get paywall ID from URL params if provided
      const urlParams = new URLSearchParams(window.location.search);
      const paywallId = urlParams.get('paywall');
      const viewId = urlParams.get('view');

      // Resolve the active checkout currency BEFORE paywall init so price resolution picks it
      // up. Geo currency is STRIPE-ONLY (the v5 multi-currency prices): Centrobill prices are
      // USD-defined (centrobill_sku_price), so a geo currency there would mismatch the
      // displayed vs charged amount. Then the md5 URL unlock (test-only: VUV) can override.
      const { PAYWALL_CONFIGS, DEFAULT_PAYWALL_ID } = await import('../config/paywall-config.js');
      const resolvedConfig = PAYWALL_CONFIGS.find(c => c.id === paywallId)
        || PAYWALL_CONFIGS.find(c => c.id === DEFAULT_PAYWALL_ID);
      if (resolvedConfig?.billing === 'stripe') {
        setCurrentCurrencyFromGeo(await waitForGeoCountry());
      }
      const activeCurrency = setCurrentCurrency(urlParams);
      if (activeCurrency !== 'USD') {
        paywallLogger.log('Active checkout currency', { currency: activeCurrency });
      }

      // Initialize paywall service
      await paywallService.initialize(paywallId || undefined);

      // Check for failed redirect and reopen checkout if needed
      paywallService.checkAndHandleFailedRedirect();

      // Check for 3DS failure (from Centrobill redirect)
      const threeDSResult = paywallService.checkAndHandle3DSFailure();

      // Switch to specific view if requested (and not overridden by 3DS failure)
      if (viewId && !threeDSResult.failed) {
        paywallService.switchView(viewId);
      }

      // Get processed view data
      const viewData = paywallService.getProcessedView();
      if (!viewData) {
        paywallLogger.error('No paywall view data available');
        return;
      }

      // Update voucher card if present
      if (viewData.voucher) {
        const voucherCard = document.getElementById('v2-voucher-card');
        const voucherCode = document.getElementById('v2-voucher-code');

        if (voucherCard) {
          voucherCard.style.display = 'block';
          if (voucherCode) voucherCode.textContent = viewData.voucher.code;
        }

        // Start voucher timer using screen config
        const timerDuration = this.currentScreen?.timer;
        if (timerDuration) {
          this.startVoucherTimer(timerDuration);
        }
      }

      // Render pricing options (pass paywallService for option selection handling)
      this.renderV2PricingOptions(viewData, paywallService);

      // Render below section (express checkout, buttons, etc.)
      await this.renderV2BelowSection(viewData, paywallService);

      // Initialize sticky header countdown and button
      this.initializeStickyHeaderTimer(paywallService);

      // Listen for checkout events
      paywallService.on('checkout:success', (data: any) => {
        paywallLogger.log('Checkout successful, navigating to success', data);

        // Store email for success page
        if (data?.email) {
          const userData = Alpine.store('userData') as any;
          if (userData) {
            userData.email = data.email;
          }
        }

        window.location.hash = '#success';
      });

      paywallService.on('checkout:error', (error) => {
        paywallLogger.error('Checkout error', error);
        const errorDiv = document.getElementById('v2-payment-error');
        if (errorDiv) {
          errorDiv.textContent = error.message || 'Payment failed. Please try again.';
        }
      });

      // Show 3DS error popup if payment failed
      if (threeDSResult.failed && threeDSResult.error) {
        this.show3DSErrorPopup(threeDSResult.error);
        paywallLogger.log('Showing 3DS failure error popup', { error: threeDSResult.error });
      }

      paywallService.on('view:switched', () => {
        const updatedViewData = paywallService.getProcessedView();
        if (updatedViewData) {
          // Update voucher card
          const voucherCard = document.getElementById('v2-voucher-card');
          const voucherCode = document.getElementById('v2-voucher-code');

          if (updatedViewData.voucher) {
            if (voucherCard) voucherCard.style.display = 'block';
            if (voucherCode) voucherCode.textContent = updatedViewData.voucher.code;

            // Restart voucher timer using screen config
            const timerDuration = this.currentScreen?.timer;
            if (timerDuration) {
              this.startVoucherTimer(timerDuration);
            }
          } else {
            if (voucherCard) voucherCard.style.display = 'none';
          }

          this.renderV2PricingOptions(updatedViewData, paywallService);
          this.renderV2BelowSection(updatedViewData, paywallService);
        }
      });

    } catch (error) {
      console.error('[ScreenController] Failed to initialize paywall:', error);
    }
  }

  startCountdownTimer(seconds) {
    // Cancel any existing timer
    if (this.countdownTimerId !== null) {
      clearTimeout(this.countdownTimerId);
      this.countdownTimerId = null;
    }

    const timerElement = this.root.querySelector('#countdown-timer');
    const voucherTimerElement = this.root.querySelector('#voucher-timer');
    const timerLabel = this.root.querySelector('.timer-label');
    const voucherExpires = this.root.querySelector('.voucher-expires');

    // Reset timer display state
    if (timerElement) {
      timerElement.style.display = '';
    }
    if (voucherTimerElement) {
      voucherTimerElement.style.display = '';
    }
    if (timerLabel) {
      timerLabel.textContent = "Your exclusive discount expires in:";
    }
    if (voucherExpires) {
      voucherExpires.textContent = "Promo code expires in:";
    }

    let remaining = seconds;

    const updateTimer = () => {
      const minutes = Math.floor(remaining / 60);
      const secs = remaining % 60;
      const timeText = `${minutes}:${secs.toString().padStart(2, '0')}`;

      if (remaining > 0) {
        if (timerElement) {
          timerElement.textContent = timeText;
        }
        if (voucherTimerElement) {
          voucherTimerElement.textContent = timeText;
        }
        remaining--;
        this.countdownTimerId = window.setTimeout(updateTimer, 1000);
      } else {
        // Timer expired - update UI
        if (timerLabel) {
          timerLabel.textContent = "No pressure, enjoy the discount";
        }
        if (voucherExpires) {
          voucherExpires.textContent = "No pressure, enjoy the discount";
        }
        // Hide the countdown timer element
        if (timerElement) {
          timerElement.style.display = 'none';
        }
        // Hide the voucher timer element
        if (voucherTimerElement) {
          voucherTimerElement.style.display = 'none';
        }

        this.countdownTimerId = null;
        document.dispatchEvent(new CustomEvent('timer:expired'));
      }
    };

    updateTimer();
  }

  openCheckout() {
    document.dispatchEvent(new CustomEvent('checkout:open'));
  }

  async resolveCheckoutEmail(): Promise<string | null> {
    let email = store.get('email');

    if (email && Email.isValid(email)) {
      return email;
    }

    email = await this.showEmailPopup();
    if (email && Email.isValid(email)) {
      return email;
    }

    return null;
  }

  updateVoucherCard(viewData) {
    const voucherCard = this.root.querySelector('.voucher-card');
    const voucherCode = this.root.querySelector('#voucher-code');
    const voucherDiscount = this.root.querySelector('#voucher-discount');
    const discountBadge = this.root.querySelector('#discount-badge');

    if (!viewData?.voucher) {
      if (voucherCard) {
        voucherCard.setAttribute('hidden', 'true');
      }
      return;
    }

    if (voucherCard) {
      voucherCard.removeAttribute('hidden');
    }
    if (voucherCode) {
      voucherCode.textContent = viewData.voucher.code;
    }
    if (voucherDiscount) {
      voucherDiscount.textContent = `${viewData.voucher.discountPercent}% OFF`;
    }
    if (discountBadge) {
      discountBadge.textContent = `SAVE ${viewData.voucher.discountPercent}% TODAY`;
    }
  }

  renderPricingOptions(viewData, isLoading = false) {
    const container = this.root.querySelector('#pricing-options');
    if (!container) return;

    // Get the pricing option template
    const template = document.getElementById('pricing-option-template') as HTMLTemplateElement;
    if (!template) {
      console.error('Pricing option template not found');
      return;
    }

    // Clear existing options
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    // If loading, show placeholders
    if (isLoading) {
      // Show 3 loading placeholders
      for (let i = 0; i < 3; i++) {
        const placeholder = document.createElement('div');
        placeholder.className = 'pricing-option loading-placeholder';
        placeholder.innerHTML = `
          <div class="pricing-option-content">
            <div class="pricing-option-left">
              <div class="pricing-option-checkbox skeleton"></div>
              <div class="pricing-option-info">
                <div class="pricing-option-title skeleton-text"></div>
                <div class="pricing-option-prices">
                  <span class="pricing-crossed skeleton-text"></span>
                  <span class="pricing-current skeleton-text"></span>
                </div>
              </div>
            </div>
            <div class="pricing-option-right">
              <div class="pricing-daily-amount skeleton-text"></div>
              <div class="pricing-daily-label skeleton-text"></div>
            </div>
          </div>
        `;
        container.appendChild(placeholder);
      }
      return;
    }

    // Render each option
    let selectedIndex = 0; // Track which option should be selected

    // Find option with badge, if any
    const badgeIndex = viewData.options.findIndex(opt => opt.badge);
    if (badgeIndex !== -1) {
      selectedIndex = badgeIndex;
    }

    viewData.options.forEach((option, index) => {
      // Clone the template
      const optionEl = template.content.cloneNode(true) as DocumentFragment;
      const optionDiv = optionEl.querySelector('.pricing-option') as HTMLElement;

      if (!optionDiv) return;

      // Set the option ID
      optionDiv.dataset.optionId = option.priceId;

      // Select the determined option
      if (index === selectedIndex) {
        optionDiv.classList.add('selected');
      }

      // Set the badge if present
      const badgeEl = optionDiv.querySelector('.pricing-option-badge') as HTMLElement;
      if (option.badge && badgeEl) {
        badgeEl.textContent = option.badge;
        badgeEl.style.display = '';
      }

      // Set the title
      const titleEl = optionDiv.querySelector('.pricing-option-title');
      if (titleEl) {
        titleEl.textContent = option.title;
      }

      // Set the subtitle
      const subtitleEl = optionDiv.querySelector('.pricing-option-subtitle');
      if (subtitleEl) {
        subtitleEl.textContent = option.subtitle || '';
      }

      // Set the prices
      const crossedPriceEl = optionDiv.querySelector('.pricing-crossed');
      if (crossedPriceEl) {
        crossedPriceEl.textContent = option.crossedPrice.formatted;
      }

      const currentPriceEl = optionDiv.querySelector('.pricing-current');
      if (currentPriceEl) {
        currentPriceEl.textContent = option.uncrossedPrice.formatted;
      }

      // Set the daily price
      const dailyPriceEl = optionDiv.querySelector('.pricing-daily-amount');
      if (dailyPriceEl) {
        dailyPriceEl.textContent = option.dailyPrice.formatted;
      }

      // Set the crossed daily price if available
      const dailyOldEl = optionDiv.querySelector('.pricing-daily-old') as HTMLElement;
      if (dailyOldEl) {
        if (option.crossedDailyPrice?.formatted) {
          dailyOldEl.textContent = option.crossedDailyPrice.formatted;
        } else {
          dailyOldEl.style.display = 'none';
        }
      }

      container.appendChild(optionEl);
    });
  }

  setupOptionSelection(paywallService) {
    const container = this.root.querySelector('#pricing-options');
    if (!container) return;

    container.addEventListener('click', (e) => {
      const option = e.target.closest('.pricing-option');
      if (!option) return;

      // Remove selected from all options
      container.querySelectorAll('.pricing-option').forEach(opt => {
        opt.classList.remove('selected');
      });

      // Add selected to clicked option
      option.classList.add('selected');

      // Update service
      const optionId = option.dataset.optionId;
      paywallService.selectOption(optionId);

      const updatedView = paywallService.getProcessedView();
      if (updatedView) {
        this.renderBelowSection(updatedView, paywallService);
      }
    });

    // Select default option
    const firstSelected = container.querySelector('.pricing-option.selected');
    if (firstSelected) {
      paywallService.selectOption(firstSelected.dataset.optionId);
    }
  }

  updateButtonState(isLoading) {
    const ctaButton = this.root.querySelector('#paywall-cta-button');
    if (!ctaButton) return;

    if (isLoading) {
      ctaButton.disabled = true;
      ctaButton.classList.add('loading');
      ctaButton.innerHTML = '<span class="loading-spinner"></span> Loading prices...';
    } else {
      ctaButton.disabled = false;
      ctaButton.classList.remove('loading');
      ctaButton.textContent = 'Get My Plan';
    }
  }

  renderBelowSection(viewData, paywallService) {
    const container = this.root.querySelector('#paywall-below-section');
    if (!container || !viewData) return;

    // Clear existing content
    container.innerHTML = '';

    // Render each item in the below section
    viewData.belowItems.forEach(item => {
      if (item.type === 'button') {
        this.renderBelowButton(container, item, paywallService);
      } else if (item.type === 'checkout') {
        this.renderInlineCheckout(container, paywallService);
      } else if (item.type === 'express-checkout') {
        this.renderExpressCheckout(container, paywallService);
      }
    });
  }

  renderBelowButton(container, buttonConfig, paywallService) {
    const button = document.createElement('button');
    button.className = 'btn-primary large glow';
    button.textContent = buttonConfig.text || 'Get My Plan';
    button.dataset.action = buttonConfig.action || 'open-checkout';

    button.addEventListener('click', async () => {
      if (button.disabled) return;

      try {
        const email = await this.resolveCheckoutEmail();
        if (!email) return;

        const mode = buttonConfig.action === 'open-checkout-popup' ? 'popup' : undefined;
        await paywallService.openCheckout(email, undefined, undefined, { mode });
      } catch (error) {
        console.error('[ScreenController] Failed to open checkout:', error);
      }
    });

    container.appendChild(button);
  }

  renderInlineCheckout(container, paywallService) {
    const inlineContainer = document.createElement('div');
    inlineContainer.className = 'inline-checkout-widget';
    inlineContainer.id = `inline-checkout-container-${Date.now()}`;

    const placeholder = document.createElement('p');
    placeholder.className = 'checkout-placeholder';
    placeholder.textContent = 'Tap to load secure checkout';
    inlineContainer.appendChild(placeholder);

    container.appendChild(inlineContainer);

    let isMounting = false;
    let hasMounted = false;

    const mountInlineCheckout = async () => {
      if (hasMounted || isMounting) return;
      isMounting = true;
      placeholder.textContent = 'Preparing checkout...';

      try {
        const email = await this.resolveCheckoutEmail();
        if (!email) {
          placeholder.textContent = 'Tap to load secure checkout';
          return;
        }

        await paywallService.openCheckout(
          email,
          undefined,
          undefined,
          { mode: 'inline', inlineContainer }
        );

        hasMounted = true;
      } catch (error) {
        console.error('[ScreenController] Failed to open inline checkout:', error);
        placeholder.textContent = 'Checkout unavailable. Please try another method.';
        placeholder.classList.add('error');
      } finally {
        isMounting = false;
        if (!hasMounted && !placeholder.classList.contains('error')) {
          placeholder.textContent = 'Tap to load secure checkout';
        }
      }
    };

    const handleInlineInteraction = async () => {
      inlineContainer.removeEventListener('pointerdown', handleInlineInteraction);
      inlineContainer.removeEventListener('focusin', handleInlineInteraction);
      await mountInlineCheckout();
      if (!hasMounted && !placeholder.classList.contains('error')) {
        inlineContainer.addEventListener('pointerdown', handleInlineInteraction, { once: true });
        inlineContainer.addEventListener('focusin', handleInlineInteraction, { once: true });
      }
    };

    inlineContainer.addEventListener('pointerdown', handleInlineInteraction, { once: true });
    inlineContainer.addEventListener('focusin', handleInlineInteraction, { once: true });
  }

  async renderExpressCheckout(container, paywallService) {
    const expressContainer = document.createElement('div');
    expressContainer.className = 'express-checkout-container';

    // Check availability of express checkout methods
    try {
      const availability = await paywallService.getExpressCheckoutAvailability();

      if (availability.applePay || availability.googlePay) {
        const label = document.createElement('div');
        label.className = 'express-checkout-label';
        label.textContent = 'Express checkout';
        expressContainer.appendChild(label);

        // Create container for Stripe Express Checkout Element
        const elementContainer = document.createElement('div');
        elementContainer.className = 'express-checkout-element';
        elementContainer.id = `express-checkout-${Date.now()}`;
        expressContainer.appendChild(elementContainer);

        container.appendChild(expressContainer);

        try {
          const email = await this.resolveCheckoutEmail();
          if (!email) {
            expressContainer.remove();
            return;
          }

          const selectedPriceId = container.closest('[data-screen]')?.dataset.selectedOption;
          await paywallService.mountExpressCheckout(elementContainer.id, email, selectedPriceId);
        } catch (error) {
          console.error('[ScreenController] Failed to mount express checkout:', error);
          const errorMsg = document.createElement('p');
          errorMsg.className = 'express-checkout-error';
          errorMsg.textContent = 'Express checkout unavailable';
          expressContainer.appendChild(errorMsg);
        }
      }
    } catch (error) {
      console.error('[ScreenController] Failed to check express checkout availability:', error);
    }
  }

  showEmailPopup() {
    return new Promise((resolve) => {
      // Get the email popup template
      const template = document.getElementById('email-popup-template');
      if (!template) {
        console.error('Email popup template not found');
        resolve(null);
        return;
      }

      // Clone and append the popup to the body
      const popupContent = template.content.cloneNode(true);
      document.body.appendChild(popupContent);

      // Get references to popup elements
      const backdrop = document.getElementById('email-modal-backdrop');
      const closeButton = document.getElementById('email-modal-close');
      const form = document.getElementById('email-popup-form');
      const emailInput = document.getElementById('popup-email-input');
      const submitButton = document.getElementById('popup-email-submit');
      const validation = document.getElementById('popup-email-validation');

      if (!backdrop || !form || !emailInput || !submitButton) {
        console.error('Email popup elements not found');
        resolve(null);
        return;
      }

      // Function to close the popup
      const closePopup = (email = null) => {
        if (backdrop && backdrop.parentNode) {
          backdrop.parentNode.removeChild(backdrop);
        }
        resolve(email);
      };

      // Close button handler
      if (closeButton) {
        closeButton.addEventListener('click', () => closePopup());
      }

      // Click outside to close
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) {
          closePopup();
        }
      });

      // Email validation on input
      emailInput.addEventListener('input', () => {
        const value = emailInput.value.trim();

        // Clear previous validation
        if (validation) {
          validation.textContent = '';
          validation.classList.remove('error');
        }

        if (value && !Email.isValid(value)) {
          submitButton.disabled = true;
          submitButton.classList.add('btn-disabled');
          if (validation) {
            validation.textContent = 'Please enter a valid email address';
            validation.classList.add('error');
          }
        } else if (value) {
          submitButton.disabled = false;
          submitButton.classList.remove('btn-disabled');
        } else {
          submitButton.disabled = true;
          submitButton.classList.add('btn-disabled');
        }
      });

      // Initialize button state
      submitButton.disabled = true;
      submitButton.classList.add('btn-disabled');

      // Form submission
      form.addEventListener('submit', (e) => {
        e.preventDefault();

        const email = emailInput.value.trim().toLowerCase();

        if (Email.isValid(email)) {
          // Store the email
          store.set('email', email);
          // Attach email as an Amplitude user property (not user_id)
          identifyAmplitude({ email });

          // Close popup and return email
          closePopup(email);
        } else {
          if (validation) {
            validation.textContent = 'Please enter a valid email address';
            validation.classList.add('error');
          }
        }
      });

      // Focus on the input field
      emailInput.focus();
    });
  }

  downloadApp() {
    // iOS App Store link (matches the Flutter app's AppEnvironment.iosStoreUrl)
    window.location.href = 'https://apps.apple.com/us/app/lunai-care/id6759287535';
  }

  showSuccessAnimation() {
    // Create confetti effect
    const confettiContainer = this.root.querySelector('#confetti');
    if (confettiContainer) {
      for (let i = 0; i < 50; i++) {
        const confetti = document.createElement('div');
        confetti.className = 'confetti-piece';
        confetti.style.left = `${Math.random() * 100}%`;
        confetti.style.animationDelay = `${Math.random() * 3}s`;
        confetti.style.backgroundColor = ['#C33C54', '#FFB140', '#3F88C5'][Math.floor(Math.random() * 3)];
        confettiContainer.appendChild(confetti);
      }
    }

    // Display user email
    const emailDisplay = this.root.querySelector('#user-email-display');
    const userEmail = store.get('email');
    if (emailDisplay && userEmail) {
      emailDisplay.textContent = userEmail;
    }
  }

  getCurrentScreen() {
    console.log('getCurrentScreen returned:', this.currentScreen);
    return this.currentScreen;
  }

  initializePredictionAnimations() {
    // Animate metric values with counter effect
    const animateCounter = (element, start, end, duration) => {
      const startTime = performance.now();
      const difference = end - start;

      const updateCounter = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        // Ease-out animation
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.round(start + difference * easeOutQuart);

        element.textContent = `${currentValue}%`;

        if (progress < 1) {
          requestAnimationFrame(updateCounter);
        }
      };

      requestAnimationFrame(updateCounter);
    };

    // Animate improvement values
    const animateImprovement = (element, improvement, duration) => {
      const startTime = performance.now();

      const updateImprovement = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = Math.round(improvement * easeOutQuart);

        element.textContent = `(+${currentValue}%)`;

        if (progress < 1) {
          requestAnimationFrame(updateImprovement);
        }
      };

      requestAnimationFrame(updateImprovement);
    };

    // Animate chart bars with dynamic values
    const chartItems = this.root.querySelectorAll('.chart-item');
    chartItems.forEach((item, index) => {
      const currentBar = item.querySelector('.bar-current');
      const targetBar = item.querySelector('.bar-target');
      const currentValueEl = item.querySelector('.current-value');
      const targetValueEl = item.querySelector('.target-value');

      if (currentBar && targetBar) {
        const currentVal = parseInt(currentBar.dataset.current || '0');
        const targetVal = parseInt(targetBar.dataset.target || '100');

        // Set initial animation delays based on index
        currentBar.style.animationDelay = `${0.3 + index * 0.1}s`;
        targetBar.style.animationDelay = `${1 + index * 0.1}s`;

        // Animate the number counters
        setTimeout(() => {
          if (currentValueEl) {
            animateCounter(currentValueEl, 0, currentVal, 1500);
          }
        }, 300 + index * 100);

        setTimeout(() => {
          if (targetValueEl) {
            animateCounter(targetValueEl, currentVal, targetVal, 1500);
          }
        }, 1000 + index * 100);
      }
    });

    // Animate metric values in comparison columns (if they exist)
    const improvedValues = this.root.querySelectorAll('.metric-value.improved');
    improvedValues.forEach((element, index) => {
      const text = element.textContent;
      const match = text?.match(/\+(\d+)%/);

      if (match) {
        const improvement = parseInt(match[1]);
        const baseValue = parseInt(text.match(/(\d+)%/)?.[1] || '0');

        // Temporarily set to base value
        element.textContent = `${baseValue}%`;

        // After a delay, show the improvement animation
        setTimeout(() => {
          const improvementSpan = document.createElement('span');
          improvementSpan.style.marginLeft = '5px';
          improvementSpan.style.color = 'var(--color-success)';
          element.appendChild(improvementSpan);
          animateImprovement(improvementSpan, improvement, 1500);
        }, 1500 + index * 200);
      }
    });

    // Animate the user count in confidence badge
    const confidenceBadge = this.root.querySelector('.confidence-badge');
    if (confidenceBadge) {
      const text = confidenceBadge.textContent;
      const match = text?.match(/(\d+,?\d+)/);

      if (match) {
        const targetNumber = parseInt(match[1].replace(',', ''));
        const formattedNumber = targetNumber.toLocaleString();

        // Create animated counter
        const animateUserCount = () => {
          const duration = 2000;
          const startTime = performance.now();

          const updateCount = (currentTime) => {
            const elapsed = currentTime - startTime;
            const progress = Math.min(elapsed / duration, 1);

            const easeOutQuart = 1 - Math.pow(1 - progress, 4);
            const currentValue = Math.round(targetNumber * easeOutQuart);

            confidenceBadge.innerHTML = text.replace(/\d+,?\d+/, currentValue.toLocaleString());

            if (progress < 1) {
              requestAnimationFrame(updateCount);
            }
          };

          requestAnimationFrame(updateCount);
        };

        // Start animation after a delay
        setTimeout(animateUserCount, 2000);
      }
    }

    // Add sparkle effects to achievement banner
    const achievementBanner = this.root.querySelector('.achievement-banner');
    if (achievementBanner) {
      // Create sparkle elements
      const createSparkle = () => {
        const sparkle = document.createElement('span');
        sparkle.style.position = 'absolute';
        sparkle.style.fontSize = '20px';
        sparkle.textContent = '✨';
        sparkle.style.left = `${Math.random() * 100}%`;
        sparkle.style.top = `${Math.random() * 100}%`;
        sparkle.style.animation = 'sparkle 2s ease-in-out forwards';
        sparkle.style.pointerEvents = 'none';

        achievementBanner.style.position = 'relative';
        achievementBanner.appendChild(sparkle);

        // Remove sparkle after animation
        setTimeout(() => sparkle.remove(), 2000);
      };

      // Add sparkles periodically
      setInterval(createSparkle, 3000);

      // Initial sparkles
      setTimeout(createSparkle, 500);
      setTimeout(createSparkle, 1000);
      setTimeout(createSparkle, 1500);
    }
  }


  renderV2PricingOptions(viewData, paywallService) {
    const containers = [
      document.getElementById('v2-pricing-options'),
      document.getElementById('v2-pricing-options-2')
    ].filter(Boolean) as HTMLElement[];
    if (containers.length === 0) return;

    const screen = containers[0].closest('[data-screen]') as HTMLElement;

    // Find the default option (first with badge, or first option)
    const defaultOptionIndex = viewData.processed_options.findIndex(opt => opt.badge) !== -1
      ? viewData.processed_options.findIndex(opt => opt.badge)
      : 0;

    // Shared handler for selecting a pricing option
    const selectPricingOption = async (option: any) => {
      // Sync selection UI across ALL containers
      containers.forEach(containerEl => {
        containerEl.querySelectorAll('.pricing-option').forEach((opt: Element) => {
          const isSelected = (opt as HTMLElement).dataset.optionId === option.price;
          opt.classList.toggle('selected', isSelected);
          opt.querySelector('.pricing-option-checkbox')?.classList.toggle('checked', isSelected);
        });
      });

      // Store selected option
      screen.dataset.selectedOption = option.price;

      // Update disclaimer prices
      this.updatePaywallDisclaimerPrices(option);

      // Update Express Checkout and Payment Element if they exist
      await this.updateExpressCheckoutForOption(option, paywallService);
    };

    // Render options to all containers
    // Get the pricing option template
    const template = document.getElementById('pricing-option-template') as HTMLTemplateElement;
    if (!template) {
      console.error('Pricing option template not found');
      return;
    }

    containers.forEach(container => {
      container.innerHTML = '';

      viewData.processed_options.forEach((option: any, index: number) => {
        // Clone the template
        const optionEl = template.content.cloneNode(true) as DocumentFragment;
        const optionDiv = optionEl.querySelector('.pricing-option') as HTMLElement;
        if (!optionDiv) return;

        optionDiv.dataset.optionId = option.price;

        // Set badge if present
        const badgeEl = optionDiv.querySelector('.pricing-option-badge') as HTMLElement;
        if (option.badge && badgeEl) {
          badgeEl.textContent = option.badge;
          badgeEl.style.display = '';
        }

        // Mark default option as selected
        const checkbox = optionDiv.querySelector('.pricing-option-checkbox') as HTMLElement;
        if (index === defaultOptionIndex) {
          optionDiv.classList.add('selected');
          checkbox?.classList.add('checked');
        }

        // Set title
        const titleEl = optionDiv.querySelector('.pricing-option-title');
        if (titleEl) {
          titleEl.textContent = option.title;
        }

        // Set subtitle
        const subtitleEl = optionDiv.querySelector('.pricing-option-subtitle');
        if (subtitleEl) {
          subtitleEl.textContent = option.subtitle || '';
        }

        // Set prices
        const pricesEl = optionDiv.querySelector('.pricing-option-prices') as HTMLElement;
        const crossedEl = optionDiv.querySelector('.pricing-crossed') as HTMLElement;
        const currentEl = optionDiv.querySelector('.pricing-current') as HTMLElement;

        if (option.processed_prices.crossed && crossedEl) {
          crossedEl.textContent = formatMoney(option.processed_prices.crossed, getCurrentCurrency());
          // Add arrow after crossed price
          const arrow = document.createElement('span');
          arrow.className = 'pricing-current';
          arrow.textContent = '→';
          pricesEl?.insertBefore(arrow, currentEl);
        } else if (crossedEl) {
          crossedEl.style.display = 'none';
        }

        if (currentEl) {
          const displayPrice = option.processed_prices.pay_now || option.processed_prices.recurring;
          currentEl.textContent = formatMoney(displayPrice, getCurrentCurrency());
          if (option.price_postfix) {
            currentEl.textContent += ` ${option.price_postfix}`;
          }
        }

        // Set daily prices
        const dailyOldEl = optionDiv.querySelector('.pricing-daily-old') as HTMLElement;
        const dailyAmountEl = optionDiv.querySelector('.pricing-daily-amount') as HTMLElement;
        const dailyLabelEl = optionDiv.querySelector('.pricing-daily-label') as HTMLElement;

        if (option.processed_prices.daily) {
          if (dailyOldEl && option.processed_prices.crossed_daily) {
            dailyOldEl.textContent = formatMoney(option.processed_prices.crossed_daily, getCurrentCurrency());
          } else if (dailyOldEl) {
            dailyOldEl.style.display = 'none';
          }
          if (dailyAmountEl) {
            dailyAmountEl.textContent = formatMoney(option.processed_prices.daily, getCurrentCurrency());
          }
        } else {
          // Hide daily price section if not available
          if (dailyOldEl) dailyOldEl.style.display = 'none';
          if (dailyAmountEl) dailyAmountEl.style.display = 'none';
          if (dailyLabelEl) dailyLabelEl.style.display = 'none';
        }

        // Add click handler
        optionDiv.addEventListener('click', () => selectPricingOption(option));

        container.appendChild(optionEl);
      });
    });

    // Set default selection state
    const defaultOption = viewData.processed_options[defaultOptionIndex];
    if (defaultOption) {
      screen.dataset.selectedOption = defaultOption.price;
      this.updatePaywallDisclaimerPrices(defaultOption);
    }
  }

  /**
   * Update Express Checkout when pricing option changes
   */
  private async updateExpressCheckoutForOption(option: any, paywallService: any): Promise<void> {
    // Must match the container mounted in renderV2BelowSection (v2-express-checkout-container).
    // Re-mounting rebuilds the express button with the newly selected option's amount AND
    // its confirm closure (which carries the option's price configs) — without this, switching
    // plans leaves Apple/Google Pay showing the default (badged) plan's price and configs.
    const expressContainer = document.getElementById('v2-express-checkout-container');
    if (!expressContainer || expressContainer.style.display === 'none' || !paywallService) return;

    try {
      const email = store.get('email');
      expressContainer.innerHTML = '';
      await paywallService.mountExpressCheckout(expressContainer, email, option.price);

      const amount = Math.round((option.processed_prices.pay_now || option.processed_prices.recurring || 0) * 100);
      if (paywallService.stripeService) {
        await paywallService.stripeService.updateAmount(amount);
      }
    } catch (error) {
      console.error('Failed to update Express Checkout:', error);
    }
  }

  /**
   * Update disclaimer prices based on selected option
   */
  private updatePaywallDisclaimerPrices(option: any): void {
    const disclaimer = document.getElementById('paywall-disclaimer');
    if (!disclaimer) return;
    updateDisclaimerPrices(disclaimer, option);
  }

  async renderV2BelowSection(viewData, paywallService) {
    const container = document.getElementById('v2-paywall-below-section');
    if (!container) return;

    const buttonsContainer = document.getElementById('v2-buttons-container');
    const buttonsContainer2 = document.getElementById('v2-buttons-container-2');
    const expressContainer = document.getElementById('v2-express-checkout-container');
    const standardContainer = document.getElementById('v2-standard-checkout-container');

    // Clear containers
    if (buttonsContainer) buttonsContainer.innerHTML = '';
    if (buttonsContainer2) buttonsContainer2.innerHTML = '';
    if (expressContainer) expressContainer.innerHTML = '';

    // Track if we need to use alternative items
    let useAlternativeItems = false;

    // First pass: check if express checkout is empty
    for (const item of viewData.below.items) {
      if (item.type === 'express-checkout') {
        if (expressContainer) {
          expressContainer.style.display = 'block';
          const email = store.get('email');
          const selectedPriceId = container.closest('[data-screen]')?.dataset.selectedOption;
          const hasExpressCheckout = await paywallService.mountExpressCheckout(
            expressContainer,
            email,
            selectedPriceId
          );

          if (!hasExpressCheckout) {
            // No express payment methods available
            expressContainer.style.display = 'none';
            console.log('No express checkout methods available, will use alternative items');
            useAlternativeItems = true;
          }
        }
      }
    }

    // Determine which items to render
    const itemsToRender = useAlternativeItems && viewData.below.alternative_items
      ? viewData.below.alternative_items
      : viewData.below.items;

    // Helper to create a button element
    const createButton = (item: any): HTMLButtonElement => {
      const button = document.createElement('button');
      let buttonStyle = 'btn-primary';
      if (item.style === 'secondary') {
        buttonStyle = 'btn-secondary';
      } else if (item.style === 'link') {
        buttonStyle = 'btn-link';
      }
      button.className = `${buttonStyle} btn-full`;
      button.textContent = item.text;

      button.addEventListener('click', async () => {
        if (item.action === 'open-checkout-popup') {
          await this.handleCheckoutButtonClick(paywallService);
        } else {
          console.log('Unknown button action:', item.action);
        }
      });

      return button;
    };

    // Render buttons to secondary container (prefers alternative_items, falls back to items)
    if (buttonsContainer2) {
      const secondaryItems = viewData.below.alternative_items || viewData.below.items;
      for (const item of secondaryItems) {
        if (item.type === 'button') {
          buttonsContainer2.appendChild(createButton(item));
        }
      }
    }

    // Render the items to primary container
    for (const item of itemsToRender) {
      if (item.type === 'button') {
        if (buttonsContainer) buttonsContainer.appendChild(createButton(item));

      } else if (item.type === 'express-checkout') {
        // Already handled in first pass

      } else if (item.type === 'checkout') {
        // Mount Standard Checkout (Payment Element) - without client secret initially
        if (standardContainer) {
          standardContainer.style.display = 'block';
          const paymentElement = document.getElementById('v2-payment-element');
          const submitBtn = document.getElementById('v2-submit-payment');
          const email = store.get('email');

          if (paymentElement) {
            // Mount Payment Element without creating subscription
            await paywallService.mountStandardCheckout(paymentElement, email);

            // Setup submit button - this is where subscription is created
            if (submitBtn) {
              submitBtn.addEventListener('click', async () => {
                const selectedOption = container.closest('[data-screen]').dataset.selectedOption;
                if (!selectedOption) {
                  const errorDiv = document.getElementById('v2-payment-error');
                  if (errorDiv) errorDiv.textContent = 'Please select a pricing plan';
                  return;
                }

                submitBtn.disabled = true;
                submitBtn.textContent = 'Processing...';

                const errorDiv = document.getElementById('v2-payment-error');
                if (errorDiv) errorDiv.textContent = '';

                try {
                  // NOW create subscription and complete payment
                  const result = await paywallService.completeCheckout(
                    selectedOption,
                    email || ''
                  );

                  if (result.success) {
                    window.location.hash = '#success';
                  } else {
                    if (errorDiv) {
                      errorDiv.textContent = result.error?.message || 'Payment failed';
                    }
                    submitBtn.disabled = false;
                    submitBtn.textContent = 'Complete Payment';
                  }
                } catch (error) {
                  console.error('Checkout error:', error);
                  if (errorDiv) {
                    errorDiv.textContent = error.message || 'Payment failed';
                  }
                  submitBtn.disabled = false;
                  submitBtn.textContent = 'Complete Payment';
                }
              });
            }
          }
        }
      }
    }
  }

  startVoucherTimer(duration: number) {
    const minutesEl = document.getElementById('v2-voucher-minutes');
    const secondsEl = document.getElementById('v2-voucher-seconds');
    if (!minutesEl || !secondsEl) return;

    const timerId = 'voucher-timer';

    // Clear existing timer if any
    if (this.voucherTimerIds.has(timerId)) {
      clearTimeout(this.voucherTimerIds.get(timerId));
      this.voucherTimerIds.delete(timerId);
    }

    let remainingTime = duration;

    const updateTimer = () => {
      if (remainingTime <= 0) {
        minutesEl.textContent = '00';
        secondsEl.textContent = '00';
        this.voucherTimerIds.delete(timerId);
        // Hide the voucher timer section when timer finishes
        const timerSection = document.querySelector('.voucher-timer-section');
        if (timerSection) {
          (timerSection as HTMLElement).style.display = 'none';
        }
        return;
      }

      const minutes = Math.floor(remainingTime / 60);
      const seconds = remainingTime % 60;
      minutesEl.textContent = minutes.toString().padStart(2, '0');
      secondsEl.textContent = seconds.toString().padStart(2, '0');

      remainingTime--;
      const id = setTimeout(updateTimer, 1000);
      this.voucherTimerIds.set(timerId, id);
    };

    updateTimer();
  }

  /**
   * Initialize sticky header timer and button for checkout screen
   */
  initializeStickyHeaderTimer(paywallService: any) {
    const timerDuration = this.currentScreen?.timer;
    if (!timerDuration) return;

    const countdownContainer = document.querySelector('.checkout-sticky-countdown');
    const minutesEl = document.getElementById('sticky-countdown-minutes');
    const secondsEl = document.getElementById('sticky-countdown-seconds');
    const stickyButton = document.getElementById('sticky-get-plan-btn');

    // Start countdown timer with split format (minutes/seconds in separate elements)
    if (minutesEl && secondsEl) {
      this.startSplitTimer('sticky-countdown', timerDuration, minutesEl, secondsEl, () => {
        // On expire: hide countdown, keep button
        if (countdownContainer) {
          countdownContainer.classList.add('hidden');
        }
      });
    }

    // Setup sticky button click handler (reuses same logic as below section buttons)
    if (stickyButton) {
      stickyButton.addEventListener('click', () => {
        this.handleCheckoutButtonClick(paywallService);
      });
    }
  }

  /**
   * Start a split timer that updates separate minute/second elements
   */
  startSplitTimer(
    timerId: string,
    duration: number,
    minutesEl: HTMLElement,
    secondsEl: HTMLElement,
    onExpire?: () => void
  ) {
    // Clear existing timer if any
    if (this.voucherTimerIds.has(timerId)) {
      clearTimeout(this.voucherTimerIds.get(timerId));
      this.voucherTimerIds.delete(timerId);
    }

    let remainingTime = duration;

    const updateTimer = () => {
      if (remainingTime <= 0) {
        minutesEl.textContent = '00';
        secondsEl.textContent = '00';
        this.voucherTimerIds.delete(timerId);
        if (onExpire) onExpire();
        return;
      }

      const minutes = Math.floor(remainingTime / 60);
      const seconds = remainingTime % 60;
      minutesEl.textContent = minutes.toString().padStart(2, '0');
      secondsEl.textContent = seconds.toString().padStart(2, '0');

      remainingTime--;
      const id = window.setTimeout(updateTimer, 1000);
      this.voucherTimerIds.set(timerId, id);
    };

    updateTimer();
  }

  /**
   * Handle checkout button click - shared between sticky header and below section buttons
   */
  async handleCheckoutButtonClick(paywallService: any) {
    const container = document.querySelector('[data-screen="paywall"]');
    const selectedOption = (container as HTMLElement)?.dataset?.selectedOption;

    if (!selectedOption) {
      console.error('No option selected');
      return;
    }

    // Collect email (shows the email popup if not already captured, e.g. when the
    // user lands directly on the paywall). Without a valid email the backend rejects
    // the setup intent and the checkout popup shows "Failed to load payment form".
    const email = await this.resolveCheckoutEmail();
    if (!email) {
      return;
    }

    try {
      await paywallService.openCheckout({
        priceId: selectedOption,
        email: email,
        metadata: buildCheckoutMetadata()
      });
    } catch (error) {
      console.error('[ScreenController] Failed to open checkout:', error);
    }
  }

  initializeCoinPiles() {
    // Clean up existing instance if any
    if (this.coinPiles) {
      this.coinPiles.dispose();
      this.coinPiles = null;
    }

    // Wait for DOM to update
    setTimeout(() => {
      const container = this.root.querySelector('#coin-piles-container');
      if (container) {
        this.coinPiles = new CoinPiles(container as HTMLElement);
      } else {
        console.error('Coin piles container not found');
      }
    }, 100);
    // Animate the info-big-text with typing effect
    const infoBigText = this.root.querySelector('.info-big-text');
    if (infoBigText) {
      this.animateTypingText(infoBigText as HTMLElement, 5000);
    }
  }

  animateTypingText(element: HTMLElement, delay: number) {
    const originalText = element.textContent || '';

    // Hide text initially
    element.textContent = '';

    setTimeout(() => {
      // Show element and clear text
      element.textContent = '';

      let currentIndex = 0;

      const typeNextChar = () => {
        if (currentIndex < originalText.length) {
          element.textContent += originalText[currentIndex];
          currentIndex++;
          setTimeout(typeNextChar, 50); // 50ms between each character
        }
      };

      typeNextChar();
    }, delay);
  }

  private initializeSocialProof(): void {
    const track = document.getElementById('social-proof-track');
    const countEl = document.getElementById('social-proof-count');
    if (!track) return;

    // Plan labels mirror the currently selected paywall/view so the carousel
    // never advertises plans the paywall doesn't offer (e.g. v5 sells weekly
    // plans, not the old 1-Month/3-Month/1-Year). Falls back to the legacy
    // labels if the config can't be resolved.
    const urlParams = new URLSearchParams(window.location.search);
    const dynamicPlans = getPaywallPlanLabels(
      urlParams.get('paywall') || undefined,
      urlParams.get('view') || undefined
    );
    const plans = dynamicPlans.length
      ? dynamicPlans
      : ['1-Month Plan', '3-Month Plan', '1-Year Plan'];
    const emailPrefixes = [
      'jsmith', 'alex', 'maria', 'tom', 'sara',
      'john', 'emma', 'mike', 'lisa', 'david', 'anna', 'chris',
      'kate', 'james', 'laura', 'ryan', 'julia', 'nick',
      'amy', 'ben', 'chloe', 'dan', 'eva', 'frank', 'grace',
      'henry', 'ivy', 'jack', 'kim', 'leo', 'mia', 'noah',
      'olivia', 'peter', 'quinn', 'rose', 'sam', 'tina', 'uma',
      'victor', 'wendy', 'xavier', 'yara', 'zoe', 'ashley',
      'brian', 'carla', 'derek', 'elena', 'felix', 'gina',
      'hannah', 'ivan', 'jenny', 'kevin', 'luna', 'max',
      'nina', 'oscar', 'paula', 'raj', 'sophie', 'tyler'
    ];

    // Create items (duplicate for seamless loop)
    const items: Array<{ email: string; plan: string }> = [];
    for (let i = 0; i < 10; i++) {
      items.push({
        email: emailPrefixes[Math.floor(Math.random() * emailPrefixes.length)] + '***@***',
        plan: plans[Math.floor(Math.random() * plans.length)]
      });
    }

    // Render items twice for seamless infinite scroll
    const renderItems = (itemsArray: Array<{ email: string; plan: string }>) => {
      return itemsArray.map(item => `
        <div class="social-proof-item">
          <span class="social-proof-email">${item.email}</span>
          <span class="social-proof-plan">${item.plan}</span>
        </div>
      `).join('');
    };

    track.innerHTML = renderItems(items) + renderItems(items);

    // Animate count periodically
    if (countEl) {
      const baseCount = 210 + Math.floor(Math.random() * 6);
      countEl.textContent = baseCount.toString();

      const updateCount = () => {
        const variation = Math.floor(Math.random() * 6);
        const newCount = baseCount + variation;
        countEl.textContent = newCount.toString();
      };

      setInterval(updateCount, 5000);
    }
  }

  private show3DSErrorPopup(errorMessage: string): void {
    // Get template
    const template = document.getElementById('payment-error-popup-template') as HTMLTemplateElement;
    if (!template) {
      console.error('Payment error popup template not found');
      return;
    }

    // Clone template
    const content = template.content.cloneNode(true) as DocumentFragment;
    const overlay = content.querySelector('.payment-error-overlay') as HTMLElement;

    // Set error message
    const messageEl = overlay.querySelector('.payment-error-message');
    if (messageEl) {
      messageEl.textContent = errorMessage;
    }

    document.body.appendChild(overlay);

    // Handle close
    const closePopup = () => {
      overlay.classList.add('closing');
      setTimeout(() => overlay.remove(), 200);
    };

    const btn = overlay.querySelector('.payment-error-btn');
    btn?.addEventListener('click', closePopup);

    // Close on backdrop click
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        closePopup();
      }
    });

    // Animate in
    requestAnimationFrame(() => {
      overlay.classList.add('active');
    });
  }

}
